import { HttpBackend, HttpClient } from "@angular/common/http";
import { Injectable, computed, inject, signal } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { Observable, catchError, map, of, shareReplay, switchMap, tap } from "rxjs";
import { injectLocale } from "../kendo-locale";

/** A country by its ISO 3166-1 alpha-2 code, named in the client's language. */
export interface Country {
    code: string;
    name: string;
    flag: string;
    /** The name without accents and in lower case, for matching what the user types. */
    key: string;
}

const NAMES_URL = (language: string) => `https://flagcdn.com/${language}/codes.json`;
const FLAG_URL = (code: string) => `https://flagcdn.com/w20/${code.toLowerCase()}.png`;
const FALLBACK_LANGUAGE = "en";

/** Entries of the lists that are no country: the European Union and the United Nations. The US states have longer codes. */
const NOT_COUNTRIES = new Set(["eu", "un"]);

/**
 * The countries with their names in the user's language, from the public lists of flagcdn.com.
 * The requests skip the app's interceptors: those would hand the session token to a third party,
 * and log the user out when the CDN cannot be reached.
 */
@Injectable({ providedIn: "root" })
export class CountriesService {
    private readonly http = new HttpClient(inject(HttpBackend));
    private readonly lists = new Map<string, Observable<Country[]>>();

    readonly language = injectLocale();
    /** Set when no list could be loaded: codes are then shown as they are. */
    readonly unavailable = signal(false);

    readonly countries = toSignal(toObservable(this.language).pipe(switchMap((language) => this.load(language))), { initialValue: [] as Country[] });
    readonly byCode = computed<Record<string, Country>>(() => Object.fromEntries(this.countries().map((country) => [country.code, country])));

    /** The countries whose name starts with `text`, or whose code is `text`; all of them for an empty text. */
    matching(text: string): Country[] {
        const key = keyOf(text);
        if (!key) return this.countries();
        return this.countries().filter((country) => country.key.startsWith(key) || country.code.toLowerCase() === key);
    }

    private load(language: string): Observable<Country[]> {
        let list = this.lists.get(language);
        if (!list) {
            list = this.fetch(language).pipe(
                catchError(() => (language === FALLBACK_LANGUAGE ? of(null) : this.fetch(FALLBACK_LANGUAGE).pipe(catchError(() => of(null))))),
                tap((countries) => {
                    this.unavailable.set(countries === null);
                    // A failed list is not kept, so the next change of language tries again.
                    if (countries === null) this.lists.delete(language);
                }),
                map((countries) => countries ?? []),
                shareReplay(1),
            );
            this.lists.set(language, list);
        }
        return list;
    }

    private fetch(language: string): Observable<Country[]> {
        const collator = new Intl.Collator(language);
        return this.http.get<Record<string, string>>(NAMES_URL(language)).pipe(
            map((names) =>
                Object.entries(names)
                    .filter(([code]) => code.length === 2 && !NOT_COUNTRIES.has(code))
                    .map(([code, name]) => ({ code: code.toUpperCase(), name: name, flag: FLAG_URL(code), key: keyOf(name) }))
                    .sort((a, b) => collator.compare(a.name, b.name)),
            ),
        );
    }
}

function keyOf(text: string): string {
    return text
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .trim()
        .toLowerCase();
}
